import React from 'react';
import {
  CircularProgress,
  Icon,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';

const DEFAULT_OPTIONS = {
  filtering: false,
  pageSize: 5,
  pageSizeOptions: [5, 10, 20],
  paging: true,
  search: true,
  sorting: true,
};

export function valueForField(row, field) {
  if (!field) return undefined;
  return String(field).split('.').reduce(
    (value, key) => (value == null ? undefined : value[key]),
    row
  );
}

function searchableValue(column, row) {
  const value = valueForField(row, column.field);
  if (column.lookup && Object.prototype.hasOwnProperty.call(column.lookup, value)) {
    return column.lookup[value];
  }
  return value == null ? '' : value;
}

export function applyLocalQuery(data, columns, query) {
  const visibleColumns = columns.filter((column) => !column.hidden);
  const search = String(query.search || '').trim().toLowerCase();
  let rows = [...(Array.isArray(data) ? data : [])];

  if (search) {
    rows = rows.filter((row) => visibleColumns.some((column) =>
      String(searchableValue(column, row)).toLowerCase().includes(search)
    ));
  }

  (query.filters || []).forEach((filter) => {
    if (!filter || filter.value == null || filter.value === '') return;
    const field = filter.column && filter.column.field;
    const expected = String(filter.value).toLowerCase();
    rows = rows.filter((row) => String(valueForField(row, field) == null
      ? ''
      : valueForField(row, field)).toLowerCase().includes(expected));
  });

  if (query.orderBy && query.orderBy.field) {
    const field = query.orderBy.field;
    const direction = query.orderDirection === 'desc' ? -1 : 1;
    const customSort = query.orderBy.customSort;
    rows.sort((left, right) => {
      if (customSort) return customSort(left, right) * direction;
      const leftValue = valueForField(left, field);
      const rightValue = valueForField(right, field);
      if (leftValue == null && rightValue == null) return 0;
      if (leftValue == null) return -direction;
      if (rightValue == null) return direction;
      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * direction;
      }
      return String(leftValue).localeCompare(String(rightValue), undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * direction;
    });
  }

  return rows;
}

function initialOrder(columns) {
  const column = columns.find((candidate) => candidate.defaultSort);
  return column
    ? { orderBy: column, orderDirection: column.defaultSort }
    : { orderBy: undefined, orderDirection: '' };
}

function actionIcon(icon) {
  if (React.isValidElement(icon)) return icon;
  if (typeof icon === 'function') {
    const ActionIcon = icon;
    return <ActionIcon />;
  }
  return <Icon>{icon || 'more_horiz'}</Icon>;
}

class DataTable extends React.Component {
  constructor(props) {
    super(props);
    const options = { ...DEFAULT_OPTIONS, ...(props.options || {}) };
    const order = initialOrder(props.columns || []);
    this.state = {
      editingRow: null,
      editValues: null,
      filters: {},
      loading: false,
      orderBy: order.orderBy,
      orderDirection: order.orderDirection,
      page: 0,
      pageSize: options.pageSize,
      remoteRows: [],
      saving: false,
      search: '',
      totalCount: 0,
    };
  }

  componentDidMount() {
    this.attachTableRef();
    if (this.isRemote()) this.loadRemote();
  }

  componentDidUpdate(previousProps) {
    if (previousProps.tableRef !== this.props.tableRef) this.attachTableRef(previousProps.tableRef);
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.requestId = (this.requestId || 0) + 1;
    clearTimeout(this.searchTimer);
    this.detachTableRef(this.props.tableRef);
  }

  options = () => ({ ...DEFAULT_OPTIONS, ...(this.props.options || {}) });

  isRemote = () => typeof this.props.data === 'function';

  visibleColumns = () => (this.props.columns || []).filter((column) => !column.hidden);

  query = () => ({
    filters: Object.keys(this.state.filters)
      .filter((field) => this.state.filters[field] !== '')
      .map((field) => ({
        column: (this.props.columns || []).find((column) => column.field === field),
        value: this.state.filters[field],
      })),
    orderBy: this.state.orderBy,
    orderDirection: this.state.orderDirection,
    page: this.state.page,
    pageSize: this.state.pageSize,
    search: this.state.search,
  });

  attachTableRef = (previousRef) => {
    this.detachTableRef(previousRef);
    if (this.props.tableRef) {
      this.tableApi = { onQueryChange: this.onQueryChange };
      this.props.tableRef.current = this.tableApi;
    }
  };

  detachTableRef = (tableRef) => {
    if (tableRef && tableRef.current === this.tableApi) tableRef.current = null;
  };

  onQueryChange = (query) => {
    if (query) {
      const filters = {};
      (query.filters || []).forEach((filter) => {
        if (filter.column && filter.column.field) filters[filter.column.field] = filter.value;
      });
      this.setState({
        filters,
        orderBy: query.orderBy,
        orderDirection: query.orderDirection || '',
        page: Number(query.page) || 0,
        pageSize: Number(query.pageSize) || this.state.pageSize,
        search: query.search || '',
      }, this.loadRemote);
      return;
    }
    this.loadRemote();
  };

  loadRemote = async () => {
    if (!this.isRemote()) return;
    const requestId = (this.requestId || 0) + 1;
    this.requestId = requestId;
    this.setState({ loading: true });
    try {
      const result = await this.props.data(this.query());
      if (this.unmounted || requestId !== this.requestId) return;
      this.setState({
        loading: false,
        page: Number(result && result.page) || 0,
        remoteRows: Array.isArray(result && result.data) ? result.data : [],
        totalCount: Number(result && result.totalCount) || 0,
      });
    } catch (error) {
      if (!this.unmounted && requestId === this.requestId) {
        this.setState({ loading: false, remoteRows: [], totalCount: 0 });
      }
    }
  };

  updateQuery = (changes) => {
    this.setState(changes, () => {
      if (this.isRemote()) this.loadRemote();
    });
  };

  handleSearch = (event) => {
    const search = event.target.value;
    clearTimeout(this.searchTimer);
    this.setState({ search, page: 0 });
    if (this.isRemote()) this.searchTimer = setTimeout(this.loadRemote, 300);
  };

  handleFilter = (column, value) => {
    this.updateQuery({
      filters: { ...this.state.filters, [column.field]: value },
      page: 0,
    });
  };

  handleSort = (column) => {
    if (!this.options().sorting || column.sorting === false || !column.field) return;
    const sameColumn = this.state.orderBy && this.state.orderBy.field === column.field;
    const direction = sameColumn && this.state.orderDirection === 'asc' ? 'desc' : 'asc';
    this.updateQuery({ orderBy: column, orderDirection: direction, page: 0 });
  };

  handlePage = (_, page) => this.updateQuery({ page });

  handlePageSize = (event) => this.updateQuery({
    page: 0,
    pageSize: Number(event.target.value),
  });

  startEdit = (row) => this.setState({ editingRow: row, editValues: { ...row } });

  cancelEdit = () => this.setState({ editingRow: null, editValues: null, saving: false });

  updateEditValue = (field, value) => this.setState((state) => ({
    editValues: { ...state.editValues, [field]: value },
  }));

  saveEdit = async () => {
    const { editable } = this.props;
    if (!editable || !editable.onRowUpdate) return;
    this.setState({ saving: true });
    try {
      await editable.onRowUpdate(this.state.editValues, this.state.editingRow);
      this.cancelEdit();
      if (this.isRemote()) this.loadRemote();
    } catch (_) {
      if (!this.unmounted) this.setState({ saving: false });
    }
  };

  editCell = (column) => {
    const value = valueForField(this.state.editValues, column.field);
    if (column.editComponent) {
      const EditComponent = column.editComponent;
      return <EditComponent value={value} onChange={(next) => this.updateEditValue(column.field, next)} rowData={this.state.editValues} />;
    }
    if (column.lookup) {
      return (
        <TextField
          select value={value == null ? '' : value}
          inputProps={{ 'aria-label': `Edit ${column.title || column.field}` }}
          onChange={(event) => this.updateEditValue(column.field, event.target.value)}
        >
          {Object.keys(column.lookup).map((key) => (
            <MenuItem key={key} value={key}>{column.lookup[key]}</MenuItem>
          ))}
        </TextField>
      );
    }
    return (
      <TextField
        inputProps={{ 'aria-label': `Edit ${column.title || column.field}` }}
        type={column.type === 'numeric' ? 'number' : 'text'}
        value={value == null ? '' : value}
        onChange={(event) => this.updateEditValue(column.field, event.target.value)}
      />
    );
  };

  displayCell = (column, row) => {
    if (column.render) return column.render(row);
    const value = valueForField(row, column.field);
    if (column.lookup && Object.prototype.hasOwnProperty.call(column.lookup, value)) {
      return column.lookup[value];
    }
    return value == null ? '' : String(value);
  };

  resolvedActions = (row) => (this.props.actions || []).map((action) =>
    (typeof action === 'function' ? action(row) : action)
  ).filter((action) => action && !action.hidden);

  renderActions = (row) => {
    const actions = this.resolvedActions(row);
    const canEdit = this.props.editable && this.props.editable.onRowUpdate;
    const isEditing = this.state.editingRow === row;
    if (!actions.length && !canEdit) return null;
    return (
      <TableCell align="right">
        {isEditing ? (
          <React.Fragment>
            <Tooltip title="Save"><span><IconButton disabled={this.state.saving} onClick={this.saveEdit} aria-label="Save"><Icon>check</Icon></IconButton></span></Tooltip>
            <Tooltip title="Cancel"><span><IconButton disabled={this.state.saving} onClick={this.cancelEdit} aria-label="Cancel"><Icon>close</Icon></IconButton></span></Tooltip>
          </React.Fragment>
        ) : canEdit ? (
          <Tooltip title="Edit"><IconButton onClick={() => this.startEdit(row)} aria-label="Edit"><Icon>edit</Icon></IconButton></Tooltip>
        ) : null}
        {!isEditing && actions.map((action, index) => (
          <Tooltip title={action.tooltip || 'Action'} key={`${action.tooltip || 'action'}-${index}`}>
            <span>
              <IconButton
                aria-label={action.tooltip || 'Action'} disabled={Boolean(action.disabled)}
                onClick={(event) => action.onClick && action.onClick(event, row)}
              >
                {actionIcon(action.icon)}
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </TableCell>
    );
  };

  render() {
    const options = this.options();
    const columns = this.visibleColumns();
    const localRows = this.isRemote()
      ? this.state.remoteRows
      : applyLocalQuery(this.props.data, this.props.columns || [], this.query());
    const totalCount = this.isRemote() ? this.state.totalCount : localRows.length;
    const rows = !this.isRemote() && options.paging
      ? localRows.slice(
        this.state.page * this.state.pageSize,
        (this.state.page + 1) * this.state.pageSize
      )
      : localRows;
    const hasActions = (this.props.actions && this.props.actions.length) ||
      (this.props.editable && this.props.editable.onRowUpdate);
    const loading = this.props.isLoading || this.state.loading;
    const emptyMessage = this.props.localization && this.props.localization.body
      ? this.props.localization.body.emptyDataSourceMessage
      : 'No records to display';

    return (
      <Paper data-testid="data-table" style={{ overflowX: 'auto' }}>
        <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">{this.props.title}</Typography>
          {options.search ? (
            <TextField
              inputProps={{ 'aria-label': 'Search' }} onChange={this.handleSearch} placeholder="Search"
              type="search" value={this.state.search}
            />
          ) : null}
        </Toolbar>
        {loading ? <div style={{ padding: 16, textAlign: 'center' }}><CircularProgress size={24} /></div> : null}
        <Table size={options.padding === 'dense' ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  align={column.align || 'left'} key={`${column.field || column.title}-${index}`}
                  style={{ ...(options.headerStyle || {}), ...(column.headerStyle || {}) }}
                >
                  {options.sorting && column.sorting !== false && column.field ? (
                    <TableSortLabel
                      active={Boolean(this.state.orderBy && this.state.orderBy.field === column.field)}
                      direction={this.state.orderBy && this.state.orderBy.field === column.field ? this.state.orderDirection || 'asc' : 'asc'}
                      onClick={() => this.handleSort(column)}
                    >
                      {column.title}
                    </TableSortLabel>
                  ) : column.title}
                </TableCell>
              ))}
              {hasActions ? <TableCell align="right">Actions</TableCell> : null}
            </TableRow>
            {options.filtering ? (
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell key={`filter-${column.field || index}`}>
                    {column.field && column.filtering !== false ? (
                      <TextField
                        inputProps={{ 'aria-label': `Filter ${column.title || column.field}` }}
                        onChange={(event) => this.handleFilter(column, event.target.value)}
                        placeholder="Filter" value={this.state.filters[column.field] || ''}
                      />
                    ) : null}
                  </TableCell>
                ))}
                {hasActions ? <TableCell /> : null}
              </TableRow>
            ) : null}
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length + (hasActions ? 1 : 0)}>{emptyMessage}</TableCell></TableRow>
            ) : rows.map((row, rowIndex) => {
              const isEditing = this.state.editingRow === row;
              return (
                <TableRow hover key={row.tableData && row.tableData.id != null ? row.tableData.id : rowIndex}>
                  {columns.map((column, columnIndex) => {
                    const editable = column.editable !== false && column.editable !== 'never';
                    return (
                      <TableCell
                        align={column.align || 'left'}
                        key={`${column.field || column.title}-${columnIndex}`}
                        style={{ ...(options.rowStyle || {}), ...(column.cellStyle || {}) }}
                      >
                        {isEditing && editable ? this.editCell(column) : this.displayCell(column, row)}
                      </TableCell>
                    );
                  })}
                  {this.renderActions(row)}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {options.paging ? (
          <TablePagination
            component="div" count={totalCount} page={this.state.page}
            onPageChange={this.handlePage} onRowsPerPageChange={this.handlePageSize}
            rowsPerPage={this.state.pageSize} rowsPerPageOptions={options.pageSizeOptions}
          />
        ) : null}
      </Paper>
    );
  }
}

DataTable.defaultProps = {
  actions: undefined,
  columns: [],
  data: [],
  editable: undefined,
  isLoading: false,
  localization: undefined,
  options: {},
  tableRef: undefined,
  title: '',
};

export default DataTable;
